import { useCallback, useEffect, useMemo, useState } from "react";
import type { AuthUser } from "../../auth/shared/types/auth";
import { listChallengeHistory } from "../../challenges/services/challengeAdminService";
import type {
  ChallengeHistoryItem,
  ChallengeStatus,
  ChallengeType,
} from "../../challenges/types/challengeAdmin";

type UseAdminChallengeHistoryParams = {
  defaultTypes: ChallengeType[];
  historyTypeFilterFromRoute?: ChallengeType;
  isTournamentsRouteView: boolean;
  token?: string | null;
  users: AuthUser[];
};

export function useAdminChallengeHistory({
  defaultTypes,
  historyTypeFilterFromRoute,
  isTournamentsRouteView,
  token,
  users,
}: UseAdminChallengeHistoryParams) {
  const [history, setHistory] = useState<ChallengeHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyNameFilter, setHistoryNameFilter] = useState("");
  const [historyDateFrom, setHistoryDateFrom] = useState("");
  const [historyDateTo, setHistoryDateTo] = useState("");
  const [historyOfficialFilter, setHistoryOfficialFilter] = useState<
    "all" | "official" | "organizer"
  >("all");
  const [historyOrganizerUserIdFilter, setHistoryOrganizerUserIdFilter] =
    useState("");
  const [historyStatusFilters, setHistoryStatusFilters] = useState<
    ChallengeStatus[]
  >([]);
  const [historyTypeFilters, setHistoryTypeFilters] = useState<ChallengeType[]>(
    historyTypeFilterFromRoute ? [historyTypeFilterFromRoute] : [],
  );

  const effectiveHistoryTypeFilters = useMemo(
    () =>
      isTournamentsRouteView
        ? historyTypeFilters
        : historyTypeFilters.filter(
            (type) =>
              String(type || "")
                .trim()
                .toLowerCase() !== "tournament",
          ),
    [historyTypeFilters, isTournamentsRouteView],
  );

  const loadHistory = useCallback(async () => {
    if (!token) return;
    setHistoryLoading(true);
    try {
      const official = isTournamentsRouteView
        ? undefined
        : historyOfficialFilter === "official"
          ? true
          : historyOfficialFilter === "organizer"
            ? false
            : undefined;
      const organizerUserId = isTournamentsRouteView
        ? undefined
        : (() => {
            const parsed = Number(historyOrganizerUserIdFilter);
            if (!Number.isFinite(parsed) || parsed <= 0) return undefined;
            return Math.floor(parsed);
          })();
      const data = await listChallengeHistory(token, {
        name: isTournamentsRouteView ? undefined : historyNameFilter,
        dateFrom: isTournamentsRouteView
          ? undefined
          : historyDateFrom || undefined,
        dateTo: isTournamentsRouteView ? undefined : historyDateTo || undefined,
        types: isTournamentsRouteView
          ? ["tournament"]
          : effectiveHistoryTypeFilters.length > 0
            ? effectiveHistoryTypeFilters
            : defaultTypes,
        official,
        organizerUserId: official === true ? undefined : organizerUserId,
      });
      setHistory(data);
    } catch (_err) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [
    token,
    historyNameFilter,
    historyDateFrom,
    historyDateTo,
    historyOfficialFilter,
    historyOrganizerUserIdFilter,
    effectiveHistoryTypeFilters,
    isTournamentsRouteView,
    defaultTypes,
  ]);

  useEffect(() => {
    if (historyOfficialFilter === "official" && historyOrganizerUserIdFilter) {
      setHistoryOrganizerUserIdFilter("");
    }
  }, [historyOfficialFilter, historyOrganizerUserIdFilter]);

  useEffect(() => {
    setHistoryTypeFilters(
      historyTypeFilterFromRoute ? [historyTypeFilterFromRoute] : [],
    );
  }, [historyTypeFilterFromRoute]);

  const historyTotal = useMemo(() => history.length, [history]);

  const toggleHistoryStatus = useCallback((status: ChallengeStatus) => {
    setHistoryStatusFilters((prev) =>
      prev.includes(status)
        ? prev.filter((item) => item !== status)
        : [...prev, status],
    );
  }, []);

  const toggleHistoryType = useCallback((type: ChallengeType) => {
    setHistoryTypeFilters((prev) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type],
    );
  }, []);

  const filteredHistory = useMemo(
    () =>
      isTournamentsRouteView
        ? history.filter(
            (item) =>
              String(item.challengeType || "")
                .trim()
                .toLowerCase() === "tournament",
          )
        : history.filter(
            (item) =>
              String(item.challengeType || "")
                .trim()
                .toLowerCase() !== "tournament" &&
              (historyStatusFilters.length === 0 ||
                historyStatusFilters.includes(item.status)) &&
              (effectiveHistoryTypeFilters.length === 0 ||
                effectiveHistoryTypeFilters.includes(item.challengeType)),
          ),
    [
      history,
      historyStatusFilters,
      effectiveHistoryTypeFilters,
      isTournamentsRouteView,
    ],
  );

  const organizerUsers = useMemo(() => {
    const list = users.filter(
      (user) => user.role === "organizer" || user.role === "admin",
    );
    list.sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || "")),
    );
    return list;
  }, [users]);

  return {
    effectiveHistoryTypeFilters,
    filteredHistory,
    historyDateFrom,
    historyDateTo,
    historyLoading,
    historyNameFilter,
    historyOfficialFilter,
    historyOrganizerUserIdFilter,
    historyStatusFilters,
    historyTotal,
    loadHistory,
    organizerUsers,
    setHistoryDateFrom,
    setHistoryDateTo,
    setHistoryNameFilter,
    setHistoryOfficialFilter,
    setHistoryOrganizerUserIdFilter,
    toggleHistoryStatus,
    toggleHistoryType,
  };
}
